/**
 * Pure helpers for the firm's custom case types: their picker values and
 * labels, a case's name, and the defaults a case of one drafts with.
 */
import { DraftType } from "@/api/types/draft";
import {
  draftingGroups,
  draftTypeLabel,
  type DefaultTemplate
} from "@/features/caseIde/workflows/defaultTemplates";
import type { FirmLibraryEntry } from "@/features/firmKnowledge/types";
import { VISAVALUE } from "@/types/visa.type";

import type { CaseCustomType, CustomCaseType } from "./types";

/** The New Case picker offers custom case types only where Custom Workflow is on. */
export const customWorkflowEnabled = (): boolean =>
  import.meta.env.REACT_APP_ENABLE_CUSTOM_TEMPLATE_WORKFLOW === "true";

export const CUSTOM_TYPE_GROUP_ID = "firm-custom";
export const CUSTOM_TYPE_GROUP_TITLE = "FIRM CUSTOM";
const VALUE_PREFIX = "custom-type:";

/** The picker value of a saved type; never a visa, so it can't be stored as one. */
export const customTypeValue = (id: string): string => `${VALUE_PREFIX}${id}`;

/** The type id a picker value names, or null for a visa. */
export const customTypeIdOf = (
  value: string | null | undefined
): string | null =>
  value?.startsWith(VALUE_PREFIX) ? value.slice(VALUE_PREFIX.length) : null;

export const customTypeLabel = (name: string): string => `Custom: ${name}`;

/** The name to save: one line, single spaces, without a typed "Custom:" prefix. */
export const cleanTypeName = (raw: string): string =>
  raw
    .replace(/^\s*custom\s*:\s*/i, "")
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");

export const MAX_TYPE_NAME = 60;

const isCustomVisa = (visa: unknown): boolean =>
  visa === VISAVALUE.CUSTOM_TEMPLATE;

/** A case's `custom_case_type`, when it is a named Custom Workflow case. */
export const caseCustomType = (
  caseRecord: { visa?: unknown; custom_case_type?: unknown } | null | undefined
): CaseCustomType | null => {
  const snap = caseRecord?.custom_case_type;
  if (!snap || typeof snap !== "object" || !isCustomVisa(caseRecord?.visa)) {
    return null;
  }
  const { id, name } = snap as Record<string, unknown>;
  if (typeof name !== "string" || !name.trim()) return null;
  return { id: typeof id === "string" && id ? id : null, name };
};

/** The live type a case started on; null for a one-off name or a deleted type. */
export const liveTypeOf = (
  snap: CaseCustomType | null,
  types: readonly CustomCaseType[]
): CustomCaseType | null =>
  snap?.id ? types.find((t) => t.id === snap.id) ?? null : null;

/**
 * "Custom: <name>" for a named Custom Workflow case: the type's current name,
 * else the name it had when the case started. Null for any other case.
 */
export const caseTypeLabel = (
  caseRecord: Parameters<typeof caseCustomType>[0],
  types: readonly CustomCaseType[] = []
): string | null => {
  const snap = caseCustomType(caseRecord);
  if (!snap) return null;
  return customTypeLabel(liveTypeOf(snap, types)?.name ?? snap.name);
};

/** The visa a case of the type drafts as: its base, else Custom Workflow. */
export const draftingVisaOf = (
  type: CustomCaseType | null,
  visa: string
): string => type?.base_visa || visa;

/**
 * The templates a case of the type starts its letters from: the type's own
 * picks first (where the entry is still in the library), then `inherited`.
 */
export const typeDefaultTemplates = (
  type: CustomCaseType | null,
  library: readonly FirmLibraryEntry[],
  inherited: DefaultTemplate[]
): DefaultTemplate[] => {
  if (!type?.letters) return inherited;
  const names = new Map(library.map((e) => [e.id, e.name]));
  const own = type.letters.flatMap((l) => {
    const name = l.template_entry_id ? names.get(l.template_entry_id) : null;
    return l.template_entry_id && name
      ? [
          {
            draftType: l.draft_type,
            label: draftTypeLabel(l.draft_type),
            name,
            entryId: l.template_entry_id
          }
        ]
      : [];
  });
  const picked = new Set(own.map((d) => d.draftType));
  return [...own, ...inherited.filter((d) => !picked.has(d.draftType))].sort(
    (a, b) => a.label.localeCompare(b.label)
  );
};

const NOT_LETTERS: ReadonlySet<string> = new Set([
  DraftType.EXHIBIT_LIST,
  DraftType.CUSTOM
]);

/** The letter types a custom case type can list for Draft Letters. */
export const LETTER_DRAFT_TYPES: readonly string[] = Object.values(
  DraftType
).filter((t) => !NOT_LETTERS.has(t));

/** The firm's drafting templates for a letter type, whatever their visa. */
export const templateChoicesFor = (
  library: readonly FirmLibraryEntry[],
  draftType: string
): { entryId: string; name: string }[] => {
  const seen = new Set<string>();
  return draftingGroups(library)
    .filter((g) => g.draftType === draftType)
    .flatMap((g) => g.entries)
    .filter((e) => (seen.has(e.id) ? false : (seen.add(e.id), true)))
    .map((e) => ({ entryId: e.id, name: e.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
};
